import classNames from 'classnames';
import React, { useState, useCallback, useLayoutEffect, useContext, useRef, useMemo, CSSProperties, useEffect } from 'react';
import { HasClassName } from '../component-utils';
import './TreeView.scss';

interface TreeViewProps extends React.PropsWithChildren<HasClassName> {
}

export function TreeView({ className, children }: TreeViewProps) {
  return <div className={classNames('TreeView-react', className)}>
    {children}
  </div>
}

interface TreeNodeProps extends React.PropsWithChildren<HasClassName> {
  getNodes?(): React.ReactNode
  initialExpanded?: boolean
  onExpand?(expand: boolean): void
  noButton?: boolean
}

export function TreeNode({ className, children, getNodes, initialExpanded, onExpand, noButton }: TreeNodeProps) {
  const [ expanded, setExpanded ] = useState(initialExpanded || false)
  const ref = useRef<HTMLLabelElement>(null)
  const ctx = useContext(NodesContext)

  useEffect(() => {
    if (initialExpanded !== undefined) {
      setExpanded(initialExpanded)
    }
  }, [ initialExpanded ])

  useLayoutEffect(() => {
    const div = ref.current
    if (!div) return

    if (ctx) {
      ctx.register(div)
    }

    return () => {
      if (ctx) {
        ctx.unregister(div)
      }
    }
  }, [ ctx ])

  useLayoutEffect(() => {
    if (ctx) {
      ctx.update()
    }
  }, [ ctx, expanded ])

  function onToggle() {
    const expand = !expanded
    setExpanded(expand)
    if (onExpand) {
      onExpand(expand)
    }
  }

  return <>
    <label ref={ref} className={classNames('TreeNode-react', className, expanded && 'expanded')}>
      <div className='treeline-hor' />
      {getNodes && !noButton && <TreeViewButton onClick={onToggle}>{expanded ? <MinusIcon /> : <PlusIcon />}</TreeViewButton>}
      {children}
    </label>
    {expanded && getNodes && <TreeNodes className='TreeView-nodes'>{getNodes()}</TreeNodes>}
  </>
}

interface TreeViewButtonProps extends React.PropsWithChildren<{}> {
  onClick?(e: React.MouseEvent): void
}

export function TreeViewButton({ onClick, children }: TreeViewButtonProps) {
  function handleClick(e: React.MouseEvent) {
    // to stop the <label> from activating twice
    e.preventDefault()

    if (onClick) {
      onClick(e)
    }
  }

  return <button className='TreeView-button' onClick={handleClick}>
    {children}
  </button>
}

interface TreeNodesProps extends React.PropsWithChildren<HasClassName> {
}

function TreeNodes({ className, children }: TreeNodesProps) {
  const nodes = useRef<HTMLElement[]>([])
  const nodesRef = useRef<HTMLDivElement>(null)
  const [ verStyle, setVerStyle ] = useState<CSSProperties>({})
  const parentCtx = useContext(NodesContext)
  const [ isDirty, setDirty ] = useState(false)

  const register = useCallback((node: HTMLElement) => {
    nodes.current.push(node)
    setDirty(true)
  }, [])

  const unregister = useCallback((node: HTMLElement) => {
    nodes.current = nodes.current.filter(n => n !== node)
    setDirty(true)
  }, [])

  const update = useCallback(() => {
    if (!nodesRef.current || !nodes.current.length) { return }
    const bottom = nodes.current.map(n => {
      const rect = n.getBoundingClientRect()
      return rect.bottom - rect.height / 2
    }).reduce((a, b) => a > b ? a : b)

    const top = nodesRef.current.getBoundingClientRect().top
    setVerStyle({ height: `${bottom - top}px` })

    if (parentCtx) { parentCtx.update() }
  }, [ parentCtx ])

  useLayoutEffect(() => {
    if (!isDirty) { return }
    setDirty(false)
    update()
  }, [ isDirty, update ])

  const context = useMemo(() => ({ register, unregister, update }), [ register, unregister, update ])

  return <NodesContext.Provider value={context}>
    <div ref={nodesRef} className={className}>
      {children}
      <div className='treeline-ver' style={verStyle} />
    </div>
  </NodesContext.Provider>
}

interface NodesContextData {
  register(node: HTMLElement): void
  unregister(node: HTMLElement): void
  update(): void
}

const NodesContext = React.createContext<NodesContextData | null>(null)

function PlusIcon() {
  return <svg viewBox='0 0 512 512'>
    <path d='M417.4,224H288V94.6c0-16.9-14.3-30.6-32-30.6c-17.7,0-32,13.7-32,30.6V224H94.6C77.7,224,64,238.3,64,256  c0,17.7,13.7,32,30.6,32H224v129.4c0,16.9,14.3,30.6,32,30.6c17.7,0,32-13.7,32-30.6V288h129.4c16.9,0,30.6-14.3,30.6-32  C448,238.3,434.3,224,417.4,224z' />
  </svg>
}

function MinusIcon() {
  return <svg viewBox='0 0 512 512'>
    <path d='M417.4,224H94.6C77.7,224,64,238.3,64,256c0,17.7,13.7,32,30.6,32h322.8c16.9,0,30.6-14.3,30.6-32  C448,238.3,434.3,224,417.4,224z' />
  </svg>
}
